import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  CreateWorkOrderDto,
  WORK_ORDER_TYPES,
  WORK_ORDER_PRIORITIES,
} from '@/api/services/work-orders.api';
import { useCreateWorkOrder, useEquipmentList, useLocationsList } from '@/api/hooks';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function WorkOrdersNew() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: 'CORRECTIVE',
    priority: 'MEDIUM',
    title: '',
    description: '',
    equipmentId: '',
    locationId: '',
    scheduledStart: '',
    scheduledEnd: '',
    estimatedHours: '',
  });

  const { data: equipmentData } = useEquipmentList({ limit: 100 });
  const { data: locationsData } = useLocationsList({ limit: 100 });
  const equipment = equipmentData?.data?.data ?? [];
  const locations = locationsData?.data ?? [];

  const createMutation = useCreateWorkOrder();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.locationId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const data: CreateWorkOrderDto = {
      type: formData.type,
      priority: formData.priority,
      title: formData.title,
      locationId: formData.locationId,
      ...(formData.description && { description: formData.description }),
      ...(formData.equipmentId && { equipmentId: formData.equipmentId }),
      ...(formData.scheduledStart && { scheduledStart: formData.scheduledStart }),
      ...(formData.scheduledEnd && { scheduledEnd: formData.scheduledEnd }),
      ...(formData.estimatedHours && { estimatedHours: parseFloat(formData.estimatedHours) }),
    };

    createMutation.mutate(data, {
      onSuccess: (result) => {
        navigate(`/work-orders/${result.data.id}`);
      },
    });
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="New Work Order"
        description="Asset, fault, and scheduling details required to raise a maintenance task."
        actions={
          <Button variant="outline" onClick={() => navigate('/work-orders')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Work Orders
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Work Order Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Work Order Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Replace hydraulic pump"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Detailed description of the work required..."
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>

          {/* Assignment */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Assignment</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc: any) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment (Optional)</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Schedule */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Schedule</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduledStart">Scheduled Start</Label>
                <Input
                  id="scheduledStart"
                  type="datetime-local"
                  value={formData.scheduledStart}
                  onChange={(e) => setFormData({ ...formData, scheduledStart: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduledEnd">Scheduled End</Label>
                <Input
                  id="scheduledEnd"
                  type="datetime-local"
                  value={formData.scheduledEnd}
                  onChange={(e) => setFormData({ ...formData, scheduledEnd: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedHours">Estimated Hours</Label>
              <Input
                id="estimatedHours"
                type="number"
                step="0.5"
                placeholder="4.0"
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/work-orders')}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.title || !formData.locationId || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Work Order'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
